

import { Component, Input, OnChanges, OnDestroy, ElementRef, NgZone } from '@angular/core';
import { DecimalPipe, DatePipe, CurrencyPipe } from '@angular/common';

import { isNil } from 'lodash';
import { Subscription, filter, debounceTime, tap } from 'rxjs';

import { SignificantDigitsPipe } from '../../../Misc/pipes/SignificantDigits/significant-digits.pipe';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';

import { DataListChartComponent } from '../DataListChart/data-list-chart.component';
import { DataPointModel } from '../../models/data-point.model';


import type { ReportSeriesModel } from '../../../Reports/models/report-series.model';
import { CurrencyConversionService } from '../../../../Core/Users/services/currency-conversion.service';

export enum CardChartComponentLayoutEnum {
    vertical,
    horizontal
}

export enum CardChartComponentHighlightColorEnum {
    volume,
    ave,
    impressions,
    engagements,
    applause,
    conversation,
    amplification,
    positive,
    negative,
    neutral,
    averageDomainAuthority,
    other,

    // LLM
    blue,
    purple,
    lightBlue,
    lighterBlue,
    orange,
    yellow,
    lightGreen,
    green,
    teal,
    aqua,
    coralPink,
    crimson,
    lavender,
    mediumBlue,
    darkPurple,
}

@Component({
    selector: 'agpr-card-chart',
    templateUrl: './card-chart.component.html',
    styleUrls: ['./card-chart.component.scss'],
    standalone: false
})
export class CardChartComponent extends DataListChartComponent implements OnChanges, OnDestroy {

    @Input() declare series: ReportSeriesModel[];
    @Input() layout: CardChartComponentLayoutEnum = CardChartComponentLayoutEnum.horizontal;
    @Input() color: CardChartComponentHighlightColorEnum;
    @Input() label: string;
    @Input() textAlignLeft: boolean = false;
    @Input() isPercentage: boolean = false;
    /** Digit format for a percentage value. Default keeps two decimals, as it always has */
    @Input() percentageFormat: string = '1.2-2';
    @Input() tooltip: string;
    @Input() bubbleTooltipSubjectLocalizationString: string = 'Social.intelligence.themeAnalytics.chart.metric';
    @Input() bubbleTooltipMetric: any;
    @Input() bubbleTooltipLocalizationPrefix: string = 'Social.intelligence.themeAnalytics.chart.bubbleTooltip';
    @Input() hidePrevious: boolean = false;
    @Input() disabled: boolean = false;
    @Input() showCurrency: boolean = false;
    @Input() tooltipIconInfo: string;
    @Input() headingLevel: number;
    @Input() numberOfDecimals: number;
    @Input() showCustomLabel: boolean = false;
    @Input() showCustomValue: boolean = false;
    @Input() showNull: boolean = false;

    override data: DataPointModel[] = [];

    bubbleTooltip: string;
    showNA: boolean = false;

    private seriesSubscriptions: Subscription[] = [];

    readonly cardChartComponentLayoutEnum = CardChartComponentLayoutEnum;
    readonly cardChartComponentHighlightColorEnum = CardChartComponentHighlightColorEnum;

    constructor(
        public elementRef: ElementRef,
        public decimalPipe: DecimalPipe,
        public currencyPipe: CurrencyPipe,
        public datePipe: DatePipe,
        public significantDigitsPipe: SignificantDigitsPipe,
        public localizationService: LocalizationService,
        public zone: NgZone,
        public currencyConversionService: CurrencyConversionService,
    ) {
        super(
            elementRef,
            decimalPipe,
            currencyPipe,
            datePipe,
            significantDigitsPipe,
            zone
        );
    }

    override ngOnChanges(): void {
        if (this.series !== this.oldSeries) {
            this.clearSeriesSubscriptions();

            if (this.series && this.series.length) {
                let datas = [];
                this.series.forEach((seriesItem, index) => {
                    if (this.label && seriesItem) {
                        seriesItem.label = this.label;
                    }
                    if (!seriesItem || !seriesItem.dataProvider) {
                        // Acts as continue in forEach
                        return;
                    }
                    this.seriesSubscriptions.push(
                        seriesItem.dataProvider.pipe(
                            debounceTime(300),
                            tap((data) => {
                                datas[index] = data;
                                this.loading = !datas.some(d => !!d);
                                if (datas.length === this.series.length) {
                                    // If all series items have been processed, clear the array to prepare for a new search cycle
                                    datas = [];
                                }
                            }),
                        ).subscribe({
                            next: (data) => {
                                if (index === 0) {
                                    this.showNA = false;
                                }
                                if (data) {
                                    if (!data[0]) {
                                        data[0] = {};
                                    }
                                    if (isNil(data[0].count)) {
                                        if (this.showNull && (index === 0)) {
                                            this.showNA = true;
                                            data[0].count = null;
                                        } else {
                                            data[0].count = 0;
                                        }
                                    }
                                    this.data[index] = data[0];
                                } else {
                                    this.data = [];
                                }

                                if (this.showCurrency) {
                                    if (!this.data[index]) {
                                        this.data = [];
                                        this.data[index] = {
                                            count: 0
                                        };
                                    } else {
                                        if (isNaN(Number(this.data[index].count))) {
                                            this.data[index].count = 0;
                                        }
                                        this.data[index].count = this.currencyConversionService.convertCurrencyWithoutISOCode(Number(this.data[index].count));
                                    }
                                }
                                if (this.data[0] && this.data[1] && +this.data[1].count !== 0) {
                                    this.data[1].percent = ((+this.data[0].count - +this.data[1].count) / +this.data[1].count) * 100;
                                    this.data[1].percent = isNaN(this.data[1].percent) ? 0 : this.data[1].percent;
                                    const metric =
                                        this.localizationService.transform(`${this.bubbleTooltipSubjectLocalizationString}.${this.bubbleTooltipMetric}`);
                                    const value = this.data[1].percent.toFixed(1);
                                    if (this.data[1].percent === 0) {
                                        this.bubbleTooltip =
                                            this.localizationService.transform(
                                                `${this.bubbleTooltipLocalizationPrefix}.neutral`,
                                                { metric: metric }
                                            );
                                    } else if (this.data[1].percent > 0) {
                                        this.bubbleTooltip =
                                            this.localizationService.transform(
                                                `${this.bubbleTooltipLocalizationPrefix}.positive`,
                                                { metric: metric, value: value }
                                            );
                                    } else {
                                        this.bubbleTooltip =
                                            this.localizationService.transform(
                                                `${this.bubbleTooltipLocalizationPrefix}.negative`,
                                                { metric: metric, value: value }
                                            );
                                    }
                                    this.data[1].percent = value;
                                    // A percentage card carries its sign into the previous value too, so the
                                    // number in brackets reads the same way as the one on the card
                                    const previous = this.isPercentage
                                        ? `${this.decimalPipe.transform(this.data[1].count, this.percentageFormat)}%`
                                        : this.significantDigitsPipe.transform(this.data[1].count, 1);
                                    this.bubbleTooltip += ` (${previous})`;
                                } else {
                                    this.bubbleTooltip = this.localizationService.transform('Social.intelligence.themeAnalytics.chart.noDataFromDateRange');
                                }
                            },
                            error: (error) => {
                                this.error = error;
                                this.loading = false;
                            }
                        })
                    );
                });
            }
        }
    }

    private clearSeriesSubscriptions() {
        this.seriesSubscriptions.forEach((sub) => sub.unsubscribe());
        this.seriesSubscriptions = [];
    }

    ngOnDestroy() {
        this.clearSeriesSubscriptions();
    }
}
